import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDateTime } from "@/lib/dates";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setAuth } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invites/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setError(err.response?.data?.detail || "This link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/invites/${token}/accept`, { password: pw });
      setAuth?.(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name?.split(" ")[0] || "there"}!`);
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not complete setup. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="accept-invite-page" className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <aside className="hidden lg:flex relative bg-emerald-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1200&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 to-emerald-950/95" />
        <div className="relative p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="ChildCheck" className="w-10 h-10" />
            <div className="font-display text-xl font-semibold">ChildCheck <span className="text-emerald-300">Ohio</span></div>
          </div>
          <div>
            <h2 className="font-display text-4xl leading-tight font-medium">Welcome to ChildCheck.</h2>
            <p className="mt-3 text-emerald-100/90 max-w-md">Live ratios, audit-ready DCY 01234s, parent app, staff clock-in — all on one screen.</p>
          </div>
          <div className="text-emerald-200/70 text-xs">One-time secure link · No plaintext password ever travels over email.</div>
        </div>
      </aside>

      <main className="grid place-items-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          {loading ? (
            <div className="py-20 grid place-items-center text-stone-500"><Loader2 className="w-6 h-6 animate-spin text-emerald-700" /></div>
          ) : error ? (
            <div data-testid="invite-error-card" className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
              <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
              <h1 className="mt-3 font-display text-2xl text-red-900">Link unavailable</h1>
              <p className="mt-2 text-sm text-red-800">{error}</p>
              <p className="mt-4 text-xs text-red-700">Ask your ChildCheck admin to resend a fresh invite.</p>
              <Button onClick={() => nav("/")} variant="outline" className="mt-6 rounded-xl" data-testid="invite-error-home-btn">Back to home</Button>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">ChildCheckOh</div>
              <h1 className="mt-2 font-display text-3xl font-medium text-stone-900">Set your password</h1>
              <p className="mt-2 text-stone-500">
                You're completing onboarding for <b className="text-stone-900">{info.center_name}</b>.
                Signing in as <b className="text-stone-900">{info.email}</b>.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">New password</Label>
                  <Input data-testid="invite-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-2 h-11" autoFocus required />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Confirm password</Label>
                  <Input data-testid="invite-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-type your new password" className="mt-2 h-11" required />
                </div>
                <Button type="submit" data-testid="invite-submit" disabled={busy} className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating your account…</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Set password & sign in</>}
                </Button>
                <p className="text-[11px] text-stone-400 text-center">
                  This link expires {fmtDateTime(info.expires_at)}.
                </p>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
